import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import MuiDivider from '@mui/material/Divider';
import { useLazyQuery } from '@apollo/client';
import { atom, useRecoilState, useRecoilCallback, useRecoilValue } from 'recoil';
import type { Snapshot } from 'recoil';
import { useTrackFormFieldEdit } from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/hooks/useTrackFormFieldEdit';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { logger } from 'modules/logger';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import { Colors, Spacings } from 'styles';
import Text from 'common/ui/Text';
import { Stack } from 'common/ui/Layout';
import { ReactComponent as AutoAwesome } from 'assets/AutoAwesome.svg';
import Tooltip from 'common/ui/Tooltip';
import Info from '@mui/icons-material/Info';
import Replay from '@mui/icons-material/Replay';
import { FF, useSplitFlag } from 'modules/feature-flags/suspense';
import { HPConfirmationModal } from '../../../../HangingProtocol/Steps/HPConfirmationModal';
import { DISCARD_CHANGES_MODAL } from '../../../../HangingProtocol/Steps/stepsModalFactory';
import {
  skipDiscardConfirmationState,
  initialHPFormStateSnapshot,
  hpFormStepsBaseline,
  steppedHPConfigurations,
  getStepsFromVersioned,
  hangingProtocolStepIndex,
} from '../../../../HangingProtocol/state';
import { useClinicalStepWriters } from '../../../../HangingProtocol/clinicalStepStore';
import { StepsConfigSection } from './StepsConfigSection';
import {
  DEFAULT_CREATE_HP_STEP,
  useFormStepOperations,
} from '../../../../HangingProtocol/hooks/useHPStepOperations';
import {
  useStepFormSync,
  stepLayoutsToLayouts,
  layoutsToStepLayouts,
} from '../../../../HangingProtocol/hooks/useStepFormSync';
import { useViewerLayoutWriters } from '../../../StudyLoader/viewerLayoutWriters';
import { preserveResolvedViewports } from '../../../../HangingProtocol/hpStepController';

import type { StepConfiguration } from '../../../../HangingProtocol/steppedHPTypes';
import { stepFromGqlFormat } from '../../../../HangingProtocol/steppedHPTypes';

import { Button } from 'common/ui/Button';
import { Field, FieldControl, FieldError } from '@ui/Field';
import type { HangingProtocol } from '../../../../HangingProtocol/useHangingProtocol';
import { broadcastHPInvalidation } from '../../../../HangingProtocol/useHangingProtocol';
import type { Layouts } from '../../../StudyLoader/viewerLoaderState';
import { layoutsForAllCasesState } from '../../../StudyLoader/viewerLoaderState';
import { hangingProtocolLayoutsState } from '../../../../ViewportsConfigurations/state';
import { generateViewportId, getLocalWindowId } from '../../../viewerUtils';
import { StudyClassifierInput } from './StudyClassifierInput';
import type { GetHangingProtocolQuery, GetStudyClassificationQuery } from 'generated/graphql';
import { ViewType } from 'generated/graphql';
import type { SubmitFormIntent } from '../constants';
import { POWERED_BY_SIRONA_CLASSIFICATIONS, SUBMIT_FORM_INTENT, isPhase4Steps } from '../constants';

import CircularProgress from '@mui/material/CircularProgress';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { StudyClassificationDisplay } from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/HPv4/StudyClassificationDisplay';
import { useCreateHangingProtocol } from '../hooks/useCreateHangingProtocol';
import { useUpdateHangingProtocol } from '../hooks/useUpdateHangingProtocol';
import { useBaseViewerData } from '../../../StudyLoader/useStudies';
import { telemetryContextFor } from 'modules/Apollo/types';
import { GET_HANGING_PROTOCOL } from 'modules/Apollo/queries';
import { useGetMatchingStudyDescriptions } from './useGetMatchingStudyDescriptions';
import { MatchingDescriptionsTable } from './MatchingDescriptionsTable';
import { useNumberOfPriors } from '../utils/useNumberOfPriors';
import {
  areStepsUnchanged,
  hangingProtocolFormStateMergeFn,
  buildStepFormViewports,
  defaultHPFormState,
  hpFormCloseState,
  flattenStudyClassification,
  generatePayload,
  hasUnsavedChanges,
  mergeStepViewportsForUpdate,
  NO_STEP_LAYOUT_CONTEXT,
  StepLayoutContextKind,
  withFormIntent,
} from './hangingProtocolFormHelpers';
import type { HangingProtocolFormState, StepLayoutContext } from './hangingProtocolFormHelpers';
import {
  isUniqueNameError,
  getHangingProtocolErrorMessage,
} from '../utils/getHangingProtocolErrorMessage';
import { BORDER_COLOR, TABLE_BG_COLOR } from './hpStyles';
import { HP_ERROR_BG, HP_ERROR_BORDER } from '../../../../HangingProtocol/Steps/Common';
import { rootTheme } from 'theme/rootTheme';
import { ScrollContainer, SCROLL_VIEWPORT_ATTR } from 'common/ui/ScrollContainer/ScrollContainer';
import { ScrollOrientation } from 'common/ui/ScrollContainer/scrollMetrics';
export type {
  ExtendedStudyCriteria,
  ExtendedSeriesCriteria,
  HangingProtocolFormState,
} from './hangingProtocolFormHelpers';
export {
  hangingProtocolFormStateMergeFn,
  defaultHPFormState,
  flattenStudyClassification,
  flattenSeriesClassification,
  generatePayload,
  hasUnsavedChanges,
  mergeStepViewportsForUpdate,
  NO_STEP_LAYOUT_CONTEXT,
  StepLayoutContextKind,
} from './hangingProtocolFormHelpers';

export const hangingProtocolFormState = atom<HangingProtocolFormState>({
  key: 'hangingProtocolFormState',
  default: defaultHPFormState,
  effects: [broadcastChannelSynchronizerEffect({ mergeFn: hangingProtocolFormStateMergeFn })],
});

/**
 * Viewports whose series classification has not settled yet, keyed by viewport id.
 *
 * The classifier is asynchronous -- a debounce plus a query -- so between typing a series description
 * and the criteria landing in form state there is a window where a viewport has source text and no
 * criteria. Submitting in that window stores the protocol with criteria-less viewports, which leaves the
 * backend only the description to match on and (correctly) produces no mirror group, since grouping
 * needs two or more members with matching criteria keys. Measured on a preview build: a create fired
 * mid-window sent `criteria=[NONE]` for every viewport while form state itself held `crit=0`, so nothing
 * downstream was losing them (VX-4813).
 *
 * **Deliberately not broadcast.** This is transient per-window timing, and a `true` left behind by a
 * window that closed mid-flight would disable submit with nothing to clear it. The cost is that the gate
 * only waits for the classifiers running in the window driving the form; in practice both windows'
 * classifiers start together and settle together, and the alternative -- cross-window pending state --
 * is the shape of bug this branch already spent a day on.
 *
 * Bounded by construction: the pending flag is `value !== debouncedValue || loading`, and a query that
 * errors clears `loading`, so a failed classification releases the gate rather than wedging it.
 */
export const pendingSeriesClassificationsState = atom<Record<string, true>>({
  key: 'pendingSeriesClassifications',
  default: {},
});

const formCss = css`
  background-color: ${Colors.gray3};
  margin: 0;
  max-height: 95vh;
  overflow: hidden;
`;

const headerCss = css`
  padding: ${Spacings.small}rem 16px 0;
  flex-shrink: 0;
`;

const bodyCss = css`
  padding: 16px 16px 0;
`;

const SectionDivider = styled(MuiDivider)`
  border-color: ${BORDER_COLOR};
  margin: 16px 0;
`;

/** Cap shared by both matching sections, so neither can push the sections below it off the form. */
const MATCHING_SECTION_MAX_HEIGHT_PX = 300;

/**
 * The matching-descriptions list scrolls within a capped height. Uses the custom indicator rather
 * than the native bar so the panel is the same width on Windows, where a native bar would otherwise
 * consume layout space and reflow the rows.
 */
const MatchingDescriptionsScroll = styled(ScrollContainer)`
  flex: 1;
  min-height: 0;
  max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;
  background-color: ${TABLE_BG_COLOR};
  border-radius: ${rootTheme.radius.xs}px;
  border: 1px solid ${BORDER_COLOR};

  /* The viewport is the element that scrolls, so the cap has to land on it. Its own height: 100% is
     indefinite here -- every ancestor up to the form body is auto-height -- which resolves to auto, so
     capping only an ancestor leaves the viewport as tall as its rows and it spills over the sections
     below instead of scrolling. */
  & > [${SCROLL_VIEWPORT_ATTR}] {
    max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;
  }
`;

/**
 * The criteria rows scroll on the same terms as the descriptions above. No background or border here: unlike
 * `MatchingDescriptionsTable`, `StudyClassificationDisplay` draws its own bordered box, and it is shared with the
 * viewport tile.
 */
const MatchingCriteriaScroll = styled(ScrollContainer)`
  flex: 1;
  min-height: 0;
  max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;

  & > [${SCROLL_VIEWPORT_ATTR}] {
    max-height: ${MATCHING_SECTION_MAX_HEIGHT_PX}px;
  }
`;

/**
 * The form body scrolls within the sidebar's fixed height. Uses the custom indicator rather than
 * the native bar for the same reason as `MatchingDescriptionsScroll` above: a native bar on Windows
 * would consume layout space and reflow the fields, making the panel a different width than on
 * macOS.
 */
const FormBodyScroll = styled(ScrollContainer)`
  flex: 1;
  min-height: 0;
`;

/**
 * The rejected-name treatment, matching the step chips: the same tinted fill and border, and the name itself in the
 * error colour. The border already came from `Field`'s invalid state; the fill and the text colour are what make it
 * read as the same state the reader sees on a duplicate step name.
 */
const nameErrorCss = css`
  [data-slot='field-control'] {
    background-color: ${HP_ERROR_BG};
    border-color: ${HP_ERROR_BORDER};
  }

  input {
    color: ${Colors.error};
  }
`;

const iconCss = css`
  width: 16px;
  height: 16px;
  color: ${Colors.gray8};
  cursor: pointer;
`;

export type HangingProtocolsFormProps = {
  // `createdHangingProtocolSmid` is the smid of the HP just created (CREATE mode only; undefined
  // for UPDATE). The widget uses it to pin the new HP as active so a criteria-tie with an existing
  // HP can't keep the server's auto-match showing the older one (VX-4713).
  onSubmit?: (HangingProtocolName: string, createdHangingProtocolSmid?: string) => void;
  onClose?: () => void;
  onError?: (err: Error) => void;
  hangingProtocol: Nullable<HangingProtocol>;
  mode: SubmitFormIntent;
  layouts: Layouts;
  studyDescriptions?: string[];
  groupedStudies?: string[];
  className: string;
  css?: FlattenSimpleInterpolation;
  /** Storybook-only props for forcing visual states. Not used in production. */
  __storyProps?: HangingProtocolsFormStoryProps;
};

/**
 * Storybook-only props for forcing visual states. Not used in production.
 *
 * The name error is only reachable through a submit the server rejects as a duplicate, so a story cannot mock its way
 * to it the way the classification errors do. Mirrors the escape hatches on `StepBar` and `StepBarChip`.
 */
export interface HangingProtocolsFormStoryProps {
  /** Renders the name field in its rejected state, with this message beneath it. */
  nameError?: string;
}

export const HangingProtocolsForm = (props: HangingProtocolsFormProps): React.ReactElement => (
  <Suspense fallback={null}>
    <HangingProtocolsFormInner {...props} />
  </Suspense>
);

const HangingProtocolsFormInner = ({
  layouts,
  hangingProtocol,
  mode,
  onSubmit,
  onClose,
  onError,
  className,
  __storyProps,
  studyDescriptions,
  groupedStudies,
}: HangingProtocolsFormProps): React.ReactElement => {
  const numberOfPriors = useNumberOfPriors({ context: APOLLO_QUERY_CONTEXT });
  // network-only: form-edit mutations only refetch GET_ALL_HANGING_PROTOCOLS, not this
  // per-id query, so cache-first could show a stale HP after a save. Revisit once the
  // save/edit flows patch or invalidate this query's cache entry (VX-4517).
  const [fetchHangingProtocol] = useLazyQuery<GetHangingProtocolQuery>(GET_HANGING_PROTOCOL, {
    fetchPolicy: 'network-only',
    context: {
      ...APOLLO_QUERY_CONTEXT,
      telemetry: {
        ...APOLLO_QUERY_CONTEXT.telemetry,
        fetchPolicy: 'network-only',
      },
    },
  });
  const { currentCase, currentCaseId } = useCurrentCaseContext();
  const createHangingProtocol = useCreateHangingProtocol({ context: APOLLO_QUERY_CONTEXT });
  const updateHangingProtocol = useUpdateHangingProtocol({ context: APOLLO_QUERY_CONTEXT });
  const { invalidateAllPrecachedStudies, refetch } = useBaseViewerData({
    context: APOLLO_QUERY_CONTEXT,
  });

  const primaryStudy = currentCase?.studies[0];

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Submit waits for the classifier rather than storing criteria-less viewports -- see
  // `pendingSeriesClassificationsState`. Bounded, so a failed classification releases rather than wedges.
  const pendingClassifications = useRecoilValue(pendingSeriesClassificationsState);
  const isClassifying = Object.keys(pendingClassifications).length > 0;
  // Tracks the mode+smid the form last initialized for (undefined = not yet initialized).
  // Prefixed with mode so CREATE and UPDATE can never collide, even if a real smid happened
  // to equal the CREATE sentinel. A genuine HP (or mode) switch re-initializes; same-HP
  // reference changes (step rebuilds from useHangingProtocol) do not wipe in-progress edits.
  const lastInitializedFormKeyRef = useRef<Nullable<string>>(undefined);
  // Bumped by every initialization pass and by unmount, so an async init that is still in flight
  // can tell it has been superseded. Without it, a fetch left over from a form the user already
  // closed resolves later and writes its steps into hpFormStepConfigurations -- a global atom that
  // outlives the form -- clobbering the freshly reopened form's steps (VX-4749).
  const initGenerationRef = useRef(0);
  // The generation whose `finishInit` actually ran. Compared against `initGenerationRef` to tell
  // "the latest init pass is still in flight" from "it finished", which the recovery effect below
  // needs and the key ref alone cannot answer -- that ref is claimed synchronously, before the fetch.
  const settledInitGenerationRef = useRef(0);
  const [hasNameBeenManuallyEdited, setHasNameBeenManuallyEdited] = useState(false);
  const [submittedNameError, setNameError] = useState<string | null>(null);
  const nameError = __storyProps?.nameError ?? submittedNameError;
  const [hasDuplicateStepNameError, setHasDuplicateStepNameError] = useState(false);
  const [formState, setFormState] = useRecoilState(hangingProtocolFormState);
  const phase = useSplitFlag(FF.HANGING_PROTOCOL_PHASE);
  const isHPStepsEnabled = isPhase4Steps(phase);
  const { applyProtocolLayouts: applyViewerLayouts } = useViewerLayoutWriters();
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [skipDiscardConfirmation, setSkipDiscardConfirmation] = useRecoilState(
    skipDiscardConfirmationState
  );
  const [initialFormSnapshot, setInitialFormSnapshot] = useRecoilState(initialHPFormStateSnapshot);
  const [initialStepSnapshot, setInitialStepSnapshot] = useRecoilState(hpFormStepsBaseline);
  const initialLayoutsRef = useRef<Layouts | null>(null);
  /**
   * The step the reader was on when the form opened, so cancelling can put them back.
   *
   * Needed because the form moves the *shared* step index while it is open -- `addStep` moves to the
   * step it created, and switching step in the form moves there too -- but it does not own that index
   * (`ownsStepIndex: false`), so nothing resets it on close. Adding a step and then discarding left
   * the index one past the end of the restored list: no chip matched it, so no step read as active
   * and the viewer fell back to the flat protocol, which is VX-4868's blank viewport. Mirrors
   * `initialLayoutsRef` above, which exists for the same reason on the layout side.
   */
  const stepIndexOnOpenRef = useRef<Nullable<number>>(null);
  const { applySavedSteps, applyProtocolName, selectStep } = useClinicalStepWriters();
  /**
   * The live step index, mirrored into a ref so the init effect can capture it without taking it as a
   * dependency -- it changes on every step navigation, and re-running init would wipe edits in
   * progress (see the guard at the top of that effect).
   */
  const liveStepIndex = useRecoilValue(hangingProtocolStepIndex);
  const liveStepIndexRef = useRef(liveStepIndex);
  liveStepIndexRef.current = liveStepIndex;
  const liveStepConfigurationsVersioned = useRecoilValue(steppedHPConfigurations);
  const liveStepConfigurations = getStepsFromVersioned(liveStepConfigurationsVersioned);

  /**
   * Restores the viewer layouts to the values captured when the form opened,
   * then clears the per-case layout override so that subsequent step navigation
   * (which only updates hangingProtocolLayoutsState) is not shadowed by a stale
   * entry in layoutsForAllCasesState.
   */
  const restoreInitialLayouts = useCallback(() => {
    if (initialLayoutsRef.current == null) return;
    applyViewerLayouts(initialLayoutsRef.current);
  }, [applyViewerLayouts]);

  // Shared step operations hook
  const stepOps = useFormStepOperations();
  const { initializeSteps, resetSteps } = stepOps;

  // Reset form step state on unmount to avoid stale data on next open
  useEffect(() => {
    return () => {
      resetSteps();
      lastInitializedFormKeyRef.current = undefined;
      initGenerationRef.current += 1;
    };
  }, [resetSteps]);

  // Step-aware form sync: saves/loads form state when switching steps in edit mode
  const { switchToStep, flushCurrentStepToConfigurations } = useStepFormSync({
    formState,
    setFormState,
  });

  // addStep/deleteStep now flush form state internally via the form flush
  // interceptor registered by useStepFormSync, so no wrapper is needed here.

  const studyClassification = useMemo(() => {
    return formState?.studyClassification ?? [];
  }, [formState]);

  const {
    captureOriginalValue: captureOriginalStudyDescription,
    trackEdit: trackStudyDescriptionEdit,
  } = useTrackFormFieldEdit<string>({
    metricName: viewer.usr.hangingProtocolStudyDescriptionEdited,
    isEnabled: true,
    additionalData: { hanging_protocol_smid: hangingProtocol?.smid ?? null },
  });

  const { captureOriginalValue: captureOriginalName, trackEdit: trackNameEdit } =
    useTrackFormFieldEdit<string>({
      metricName: viewer.usr.hangingProtocolNameEdited,
      isEnabled: true,
      additionalData: { hanging_protocol_smid: hangingProtocol?.smid ?? null },
    });

  const { descriptions: matchingDescriptions, loading: matchingDescriptionsLoading } =
    useGetMatchingStudyDescriptions(formState.studyCriteriaSourceText);

  const handleStudyClassificationChange = useCallback(
    (classification: GetStudyClassificationQuery['studyClassification']) => {
      const flattenedClassification = classification?.classification
        ? flattenStudyClassification(classification.classification)
        : [];

      setFormState((prev) => ({
        ...prev,
        studyClassification: flattenedClassification,
        ontologyVersion: classification?.classifierMetadata?.ontologyVersion ?? '',
      }));
    },
    [setFormState]
  );

  const handleProtocolNameChange = useCallback(
    (name: string) => {
      setNameError(null);
      captureOriginalName(formState.hangingProtocolName);
      setHasNameBeenManuallyEdited(true);
      setFormState((prev) => ({
        ...prev,
        hangingProtocolName: name,
      }));
    },
    [setFormState, captureOriginalName, formState.hangingProtocolName]
  );

  const handleStudyCriteriaSourceTextChange = useCallback(
    (description: string) => {
      captureOriginalStudyDescription(formState.studyCriteriaSourceText);
      setFormState((prev) => ({
        ...prev,
        studyCriteriaSourceText: description,
      }));

      // QOL: If user is creating a new HP, update name when description changes

      if (mode === SUBMIT_FORM_INTENT.SAVE && !hasNameBeenManuallyEdited) {
        // The name is about to change, so a "name is not unique" error from an earlier submit no
        // longer describes it. `nameError` is only ever set for that one error (see the submit catch),
        // and it also disables Create -- so leaving it set here left the button disabled after the
        // user had already fixed the collision. While the name is auto-generated, editing the
        // description is the *only* way to fix it, which made the dialog a dead end (VX-4530).
        setNameError(null);
        setFormState((prev) => ({
          ...prev,
          hangingProtocolName: description,
        }));
      }
    },
    [
      mode,
      setFormState,
      hasNameBeenManuallyEdited,
      captureOriginalStudyDescription,
      formState.studyCriteriaSourceText,
    ]
  );

  const closeAndReset = useCallback(() => {
    setHasNameBeenManuallyEdited(false);
    restoreInitialLayouts();
    // Put the reader back on the step they were on when the form opened. Only the close-without-
    // persisting paths reach here -- cancel, discard, and an Update with nothing changed -- and all
    // three discard the form's navigation along with its edits. A successful submit does not call
    // this; it keeps the reader on the step it just wrote (VX-4868).
    if (stepIndexOnOpenRef.current != null) {
      selectStep(stepIndexOnOpenRef.current);
      stepIndexOnOpenRef.current = null;
    }
    stepOps.dismissPendingUndoToasts();
    setFormState(hpFormCloseState);
    setInitialFormSnapshot(null);
    setInitialStepSnapshot(null);
    onClose?.();
  }, [
    setFormState,
    restoreInitialLayouts,
    selectStep,
    stepOps,
    setInitialFormSnapshot,
    setInitialStepSnapshot,
    onClose,
  ]);

  /**
   * The HP-owned stored layouts (active step's, or a flat HP's own) plus the layouts a save would
   * write, read from the same source flushCurrentStepToConfigurations/generatePayload read so the
   * two cannot disagree.
   *
   * Answers `{ kind: 'none' }` on CREATE (no existing HP layout to diff against), while steps are
   * enabled but not yet resolved (see the guard below), and on an UPDATE whose HP has no stored
   * `data.layouts`. Otherwise an UPDATE resolves to `ActiveStep` or `FlatHp` so a layout-only edit
   * is not invisible to hasUnsavedChanges (VX-4771, VX-4938).
   *
   * Both dirty checks below go through here, so they cannot disagree about what a save would write.
   */
  const readStepLayoutContext = useCallback(
    (snapshot: Snapshot): StepLayoutContext => {
      const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
      const caseLayouts = currentCaseId != null ? allCaseLayouts[currentCaseId] : null;
      const currentLayouts =
        caseLayouts ?? snapshot.getLoadable(hangingProtocolLayoutsState).getValue();

      if (isHPStepsEnabled) {
        // Steps load asynchronously (stepOps.steps starts null and is populated by a later
        // effect). While unresolved, this HP's stepped-ness is unknown, so a UPDATE for a
        // genuinely stepped HP must not fall through to the FlatHp branch below: its
        // hangingProtocol.data.layouts is a stale fallback the backend ignores once steps exist
        // (see generatePayload's comment), and diffing against it would compare the wrong value.
        // Staying on `none` here is safe -- flushCurrentStepToConfigurations() runs after steps
        // resolve, well before a real submit reaches the server.
        //
        // The one exception is a *known*-flat HP: `hangingProtocol.steps` (the fetched HP's own
        // step list, distinct from stepOps.steps) already reports zero steps, so there is nothing
        // for stepOps.steps to resolve into for this HP and waiting for it would never happen.
        // That case skips straight past the stepOps.steps indexing below -- it must not read
        // stepOps.steps while it is still null.
        if (stepOps.steps == null) {
          const hpSteps = hangingProtocol?.steps;
          const hpIsKnownFlat = Array.isArray(hpSteps) && hpSteps.length === 0;
          if (!hpIsKnownFlat) return NO_STEP_LAYOUT_CONTEXT;
        } else {
          const activeStep =
            stepOps.steps[snapshot.getLoadable(hangingProtocolStepIndex).getValue()];
          if (activeStep != null) {
            return {
              kind: StepLayoutContextKind.ActiveStep,
              activeStepLayouts: activeStep.layouts,
              currentLayouts,
            };
          }
        }
      }

      if (mode === SUBMIT_FORM_INTENT.UPDATE && hangingProtocol?.data?.layouts != null) {
        const storedLayouts = Object.fromEntries(
          hangingProtocol.data.layouts.map(({ id, layout }) => [id, layout.join(',')])
        );
        return {
          kind: StepLayoutContextKind.FlatHp,
          storedLayouts,
          currentLayouts,
        };
      }

      return NO_STEP_LAYOUT_CONTEXT;
    },
    [isHPStepsEnabled, stepOps.steps, currentCaseId, mode, hangingProtocol]
  );

  // Cancel reads the same layout state as submit. Without it a user who changed only a step's grid
  // got no discard prompt, and closeAndReset -> restoreInitialLayouts threw the change away in
  // silence -- the VX-4771 failure on the Cancel path rather than the Update path.
  const handleCancelClick = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const changesDetected = hasUnsavedChanges({
          currentFormState: formState,
          initialFormState: initialFormSnapshot as HangingProtocolFormState | null,
          currentStepConfigurations: stepOps.steps,
          initialStepConfigurations: initialStepSnapshot,
          stepContext: readStepLayoutContext(snapshot),
        });

        if (!changesDetected || skipDiscardConfirmation) {
          closeAndReset();
        } else {
          setShowDiscardModal(true);
        }
      },
    [
      formState,
      initialFormSnapshot,
      stepOps,
      initialStepSnapshot,
      skipDiscardConfirmation,
      closeAndReset,
      readStepLayoutContext,
    ]
  );

  const handleDiscardChanges = useCallback(() => {
    setShowDiscardModal(false);
    closeAndReset();
  }, [closeAndReset]);

  const handleKeepEditing = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleSubmit = useRecoilCallback(
    ({ snapshot }) =>
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const currentFormState = snapshot.getLoadable(hangingProtocolFormState).getValue();

        // If no changes were made in the form (UPDATE mode only), close
        // without firing a mutation. This preserves clinical dirty state.
        if (
          mode === SUBMIT_FORM_INTENT.UPDATE &&
          !hasUnsavedChanges({
            currentFormState,
            initialFormState: initialFormSnapshot as HangingProtocolFormState | null,
            currentStepConfigurations: stepOps.steps,
            initialStepConfigurations: initialStepSnapshot,
            stepContext: readStepLayoutContext(snapshot),
          })
        ) {
          closeAndReset();
          return;
        }

        trackNameEdit(currentFormState.hangingProtocolName);
        trackStudyDescriptionEdit(currentFormState.studyCriteriaSourceText);
        setIsSubmitting(true);

        // Smid of the HP created in this submit (CREATE only). Passed to onSubmit so the widget
        // can pin it as active, defeating a criteria-tie auto-match to an older HP (VX-4713).
        let createdHangingProtocolSmid: string | undefined;

        try {
          // Dismiss undo toasts immediately so the user can't revert step
          // changes while the mutation is in flight.
          stepOps.dismissPendingUndoToasts();

          // Persist the current step's form state into hpFormStepConfigurations before building payload.
          const flushedStepConfigurations = flushCurrentStepToConfigurations();

          // A `steps` array replaces the HP's entire step collection backend-side, minting new step
          // smids even when the only thing that changed was the HP's name. Omit it when the steps
          // are untouched, so a name- or criteria-only edit leaves the step rows (and every piece of
          // client bookkeeping keyed off their smids) alone (VX-4766). CREATE always sends steps:
          // there is nothing to preserve, and the flag would produce a stepless HP.
          const preserveExistingSteps =
            mode === SUBMIT_FORM_INTENT.UPDATE &&
            areStepsUnchanged(flushedStepConfigurations, initialStepSnapshot);

          const payload = generatePayload({
            formState: currentFormState,
            layouts,
            numberOfPriors,
            steps: isHPStepsEnabled ? flushedStepConfigurations : null,
            studyDescriptions,
            groupedStudies,
            preserveExistingSteps,
          });

          let serverStepSmids: Array<string | null> | null = null;
          if (mode === SUBMIT_FORM_INTENT.UPDATE) {
            if (hangingProtocol == null || hangingProtocol.smid == null) {
              throw new Error('Cannot update: hanging protocol not found');
            }

            const updateResult = await updateHangingProtocol({
              variables: {
                id: hangingProtocol.smid,
                payload: payload,
              },
            });
            // The server assigns real smids to steps this save created; the flushed steps still
            // carry the `step-{uuid}` placeholders the form generated. Keeping those is not
            // cosmetic -- `revertAllStepsToBaseline` treats a client-generated smid as "never
            // saved" and drops the step, so a later Reset All silently deleted a step the user had
            // already saved. Observed as a step bar losing a chip, with the baseline holding
            // ["<uuid>", "step-<uuid>"] and only the first surviving the filter.
            serverStepSmids =
              updateResult?.data?.updateHangingProtocol?.steps?.map((step) => step.smid ?? null) ??
              null;

            try {
              analytics.track(viewer.usr.hangingProtocolUpdated, {
                hanging_protocol_name: payload.name,
                hanging_protocol_smid: hangingProtocol.smid,
                intent: 'update',
                hanging_protocol_payload: {
                  name: payload.name,
                  study_descriptions: payload.studyDescriptions,
                  number_of_priors: payload.numberOfPriors,
                  study_criteria: payload.studyCriteria,
                  layouts: payload.data.layouts,
                  viewports_configurations: payload.data.viewportsConfigurations,
                },
              });
            } catch (analyticsError) {
              logger.error('Failed to track hanging protocol update analytics', { analyticsError });
            }
          } else {
            const result = await createHangingProtocol({
              variables: {
                payload: payload,
              },
            });

            if (result.data?.createHangingProtocol) {
              createdHangingProtocolSmid = result.data.createHangingProtocol.smid ?? undefined;
              try {
                analytics.track(viewer.usr.hangingProtocolCreated, {
                  hanging_protocol_name: payload.name,
                  hanging_protocol_smid: result.data.createHangingProtocol.smid,
                  intent: 'create',
                  hanging_protocol_payload: {
                    name: payload.name,
                    study_descriptions: payload.studyDescriptions,
                    number_of_priors: payload.numberOfPriors,
                    study_criteria: payload.studyCriteria,
                    layouts: payload.data.layouts,
                    viewports_configurations: payload.data.viewportsConfigurations,
                  },
                });
              } catch (analyticsError) {
                logger.error('Failed to track hanging protocol create analytics', {
                  analyticsError,
                });
              }
            }
          }
          // Store dirty step data for steps not edited in the form into the
          // pending restore atom BEFORE refetch. The reinit effect in
          // useHangingProtocol reads and merges this data when it writes
          // Success - close the widget and reset state.
          if (isHPStepsEnabled && flushedStepConfigurations != null) {
            // The form works in the protocol's stored shape, which is criteria-only: study_smid,
            // series_smid and stack_smid are null, and the server-side match is what resolves them
            // against the studies actually open. Writing the flushed steps in raw would replace the
            // resolved assignments with nulls and blank every viewport until a refresh re-ran the
            // match, because the re-init guard in useHangingProtocol stops the refetch below from
            // repairing them (VX-4764). Carry the resolution forward from what the viewer already
            // has instead.
            // Read the resolved steps from a snapshot rather than from the setter's `prev`: a Recoil
            // updater has to stay pure and may run more than once, so it is the wrong place to
            // compute a value the baseline write below also needs.
            const resolvedSteps = getStepsFromVersioned(
              snapshot.getLoadable(steppedHPConfigurations).getValue()
            );
            const merged = preserveResolvedViewports(flushedStepConfigurations, resolvedSteps);
            // Adopt the identities the server just handed back, positionally: the payload sends
            // steps in order and the backend recreates them in that order, so index is the mapping.
            // Only applied when the counts agree -- if they do not, this save is not the authority
            // on which step is which and the next match will reconcile them.
            if (serverStepSmids != null && serverStepSmids.length === merged.steps.length) {
              merged.steps = merged.steps.map((step, i) => ({
                ...step,
                smid: serverStepSmids![i] ?? step.smid,
              }));
            }
            // Split warn/info deliberately so the actionable case is queryable on its own. An
            // unresolved viewport renders blank until the next server match: expected right after
            // the user retargets one, a regression of VX-4764 if it shows up on an untouched save.
            const resolutionLog = {
              hangingProtocolSmid: hangingProtocol?.smid,
              reattached: merged.reattached,
              unresolved: merged.unresolved,
              steps: merged.steps.length,
            };
            if (merged.unresolved > 0) {
              logger.warn(
                '[HP] steps saved with viewports that resolve to no series',
                resolutionLog
              );
            } else if (merged.reattached > 0) {
              logger.info('[HP] carried resolved viewports through save', resolutionLog);
            }
            // One operation, both atoms. The dirty indicator diffs the steps against the baseline,
            // so writing one and not the other left every step the user had just saved reading as
            // unsaved (VX-4764). Keeping the two in step also means a later reinit sees no local
            // edits to preserve for these steps, and can take the server's copy. The merged steps
            // are what gets baselined, not the flushed ones, or the re-attached smids read as dirt.
            applySavedSteps({
              steps: merged.steps,
              name: currentFormState.hangingProtocolName,
            });
          } else {
            applyProtocolName({ name: currentFormState.hangingProtocolName });
          }
          const hpMatchAfterInvalidate = await invalidateAllPrecachedStudies();
          await refetch();
          broadcastHPInvalidation();

          // Only ask the widget to pin the created HP when the server's post-create match did NOT
          // land on it -- i.e. a criteria tie left an existing same-criteria HP winning the match
          // (VX-4713). In the common case the new HP becomes the match on its own, so we pass
          // undefined and the widget skips pinning entirely. That keeps this path a no-op confirmed
          // write for ordinary creates, which matters: unconditionally pinning perturbed the
          // timing-sensitive stepped-HP edit-form init and regressed the live e2e.
          const matchedSmidAfterCreate =
            hpMatchAfterInvalidate?.data?.matchHangingProtocolByWorklistsAndStudies?.smid ?? null;
          const createdHangingProtocolNeedsPin =
            createdHangingProtocolSmid != null &&
            matchedSmidAfterCreate !== createdHangingProtocolSmid;

          onSubmit?.(
            currentFormState.hangingProtocolName,
            createdHangingProtocolNeedsPin ? createdHangingProtocolSmid : undefined
          );
          setHasNameBeenManuallyEdited(false);
          setFormState(hpFormCloseState);
          setInitialFormSnapshot(null);
          setInitialStepSnapshot(null);
        } catch (err) {
          if (isUniqueNameError(err)) {
            setNameError(getHangingProtocolErrorMessage(err, mode));
          }
          onError?.(err as Error);
          return;
        } finally {
          // Reset on both success and error paths; the form can stay mounted
          // after submit, so the success path must clear the spinner too.
          setIsSubmitting(false);
        }
      },
    [
      closeAndReset,
      initialFormSnapshot,
      initialStepSnapshot,
      createHangingProtocol,
      flushCurrentStepToConfigurations,
      hangingProtocol,
      invalidateAllPrecachedStudies,
      isHPStepsEnabled,
      refetch,
      layouts,
      mode,
      numberOfPriors,
      onError,
      onSubmit,
      setFormState,
      setInitialFormSnapshot,
      setInitialStepSnapshot,
      readStepLayoutContext,
      trackNameEdit,
      trackStudyDescriptionEdit,
      updateHangingProtocol,
      applySavedSteps,
      applyProtocolName,
      stepOps,
      studyDescriptions,
      groupedStudies,
    ]
  );

  /**
   * Initialize form state. Priority order:
   * 1. Existing hanging protocol
   * 2. Primary study
   * 3. Default HP form state (empty/nullish values)
   *
   * Note that viewports get handled separately in ViewportClassifications
   */
  // Clinical edits (rename, add, delete) persist to the backend and update
  // steppedHPConfigurations via Recoil. The Apollo cache also receives updates
  // from mutation responses. A refetch on form open was previously attempted
  // here but caused layout resets (the invalidateHPInitializationCache +
  // refetch chain overwrites hangingProtocolLayoutsState asynchronously,
  // which conflicts with restoreInitialLayouts on form close).

  useEffect(() => {
    // Guard against re-initialization on same-HP reference changes (e.g. useHangingProtocol
    // rebuilds hangingProtocol per step index), which would otherwise wipe in-progress edits.
    // UPDATE keys on the real smid. CREATE has no real HP identity -- hangingProtocol is just
    // whatever incidentally auto-matches the case -- so it ignores smid entirely; otherwise an
    // unrelated change to that match (e.g. a delete elsewhere) could trip the guard mid-create
    // and reset studyClassification without the classifier query refiring. Prefixed with mode
    // so CREATE and UPDATE keys can never collide, regardless of what a real smid contains.
    const currentInitKey =
      mode === SUBMIT_FORM_INTENT.UPDATE ? `update:${hangingProtocol?.smid ?? ''}` : 'create';
    if (lastInitializedFormKeyRef.current === currentInitKey) return;
    lastInitializedFormKeyRef.current = currentInitKey;
    const initGeneration = ++initGenerationRef.current;

    // Eagerly set the form intent so that HangingProtocolViewport init effects
    // (which check prev.submitFormIntent inside their functional updater) see
    // the correct mode and skip their CREATE-mode initialization. Without this,
    // viewports briefly populate from the viewer's matched series (correct) and
    // then get overwritten when the async fetchAndInit completes with step data.
    //
    // withFormIntent also sets hpSmid and clears closeSignal -- see its doc comment.
    // Without clearing closeSignal, a `prev` still carrying `closeSignal: true` from the last
    // close (hpFormCloseState, written by closeAndReset) would ride along into this broadcast,
    // and the merge fn (hangingProtocolFormStateMergeFn) treats any `closeSignal: true` as an
    // authoritative close, resetting every window (including this one) back to the default
    // "creating" state regardless of the `mode` just set here (VX-3589).
    setFormState((prev) => withFormIntent(prev, mode, hangingProtocol?.smid ?? null));

    // Capture the current layouts so we can restore them if the user cancels
    initialLayoutsRef.current = layouts;
    // Same, for the step the reader is on. Read through the ref so this effect does not depend on an
    // index that changes with every step navigation.
    stepIndexOnOpenRef.current = liveStepIndexRef.current;

    // Reset the manual edit flag when form initializes
    setHasNameBeenManuallyEdited(false);

    /**
     * Finishes form initialization once step configurations are resolved.
     * Extracted so both the sync (create) and async (update) paths share
     * the same viewport / form-state setup logic.
     */
    const finishInit = (initialSteps: StepConfiguration[] | null) => {
      settledInitGenerationRef.current = initGeneration;

      /**
       * The step the form opens on: the one the reader was already on, not step 0 (VX-4810).
       *
       * Opening on 0 was three bugs in one. It moved the reader off their step; it seeded the form's
       * viewports and published the grid from step 0 while `hangingProtocolStepIndex` still pointed at
       * step N, so the chips showed N active over step 0's content; and `addStep` clones
       * `currentSteps[currentStepIndex]`, so Add Step duplicated step 0. Step 1 is usually left at the
       * 2x2 default, which is why the report reads as "Add Step defaults to 2x2 on every window" --
       * measured on a two-step protocol whose step 2 was 1x3: opening the form dropped window 0 to 1,1
       * and the clone came out 1,1.
       *
       * Clamped by `initializeSteps`, which is also where the index is written.
       */
      const openedOnStepIndex = Math.max(stepIndexOnOpenRef.current ?? 0, 0);
      const initialStepIndex =
        initialSteps != null && initialSteps.length > 0
          ? Math.min(openedOnStepIndex, initialSteps.length - 1)
          : 0;

      initializeSteps(initialSteps, { stepIndex: initialStepIndex });

      // NOTE: Do NOT write initialSteps to steppedHPConfigurations (viewer atom)
      // here. The raw-fetched step data may have different layouts than the
      // current viewer, which would trigger useHangingProtocol's memo to rebuild
      // and change the viewport grid immediately on form open. The viewer atom
      // is synced on step switch (via switchToStep) and on save (via submit handler).

      // Build viewport form state. When steps exist, read directly from step 0
      // instead of hangingProtocol.viewportsConfigurations, which is derived from
      // the Recoil stepIndex and may still reflect the previous step on this render
      // (initializeSteps moves the index to the opening step, but the memo won't recompute until the
      // next render).
      const viewportsState: HangingProtocolFormState['viewports'] = {};

      if (initialSteps != null && initialSteps.length > 0) {
        // CREATE speaks only for this window; every other window seeds its own viewports from what
        // it has hung. See buildStepFormViewports for why claiming them all blanks viewer/1.
        Object.assign(
          viewportsState,
          buildStepFormViewports({
            step: initialSteps[initialStepIndex],
            layouts,
            restrictToWindowId: mode === SUBMIT_FORM_INTENT.SAVE ? getLocalWindowId() : undefined,
          })
        );
      } else {
        if (hangingProtocol?.viewportsConfigurations != null) {
          hangingProtocol.viewportsConfigurations.forEach((config) => {
            viewportsState[config.id] = {
              ...viewportsState[config.id],
              seriesCriteriaSourceText: config.configuration.seriesCriteriaSourceText ?? '',
              comparisonIndex: config.configuration.comparisonIndex ?? null,
              viewType: config.configuration.viewType ?? ViewType.TwoDDre,
              isClearViewport: false,
              // seriesClassificationCriteria: Intentionally omitted. Display name is not persisted, and needs to be re-fetched from the server. This will happen after form state is set and debounce expires.
            };
          });
        }

        // When editing a non-stepped HP, viewports without configurations were cleared.
        // Mark them as isClearViewport: true by checking all viewport positions in the layout.
        if (mode === SUBMIT_FORM_INTENT.UPDATE && hangingProtocol?.data?.layouts != null) {
          hangingProtocol.data.layouts.forEach(({ id: windowId, layout }) => {
            const [rows, cols] = layout;
            for (let row = 0; row < rows; row++) {
              for (let col = 0; col < cols; col++) {
                const viewportId = generateViewportId(windowId, row, col);
                // If viewport has no configuration, it was cleared
                if (viewportsState[viewportId] == null) {
                  viewportsState[viewportId] = {
                    seriesCriteriaSourceText: '',
                    comparisonIndex: null,
                    viewType: ViewType.TwoDDre,
                    seriesClassificationCriteria: [],
                    isClearViewport: true,
                  };
                }
              }
            }
          });
        }
      }

      const hangingProtocolName =
        (mode === SUBMIT_FORM_INTENT.UPDATE ? hangingProtocol?.name : primaryStudy?.description) ??
        '';

      const studyCriteriaSourceText =
        (mode === SUBMIT_FORM_INTENT.UPDATE
          ? hangingProtocol?.studyCriteriaSourceText
          : primaryStudy?.description) ?? '';

      const initialFormState: HangingProtocolFormState = {
        hangingProtocolName,
        studyCriteriaSourceText,
        studyClassification: [],
        ontologyVersion: '',
        submitFormIntent: mode,
        // Discriminator for the cross-window merge fn: the HP smid when editing,
        // null when creating. Lets the merge distinguish a same-HP broadcast race
        // from a case/HP switch.
        hpSmid: mode === SUBMIT_FORM_INTENT.UPDATE ? (hangingProtocol?.smid ?? null) : null,
        viewports: viewportsState,
      };

      setFormState((prev) => ({
        ...prev,
        ...initialFormState,
        // See the eager-set write above: `prev` can still carry a stale `closeSignal: true`
        // from the last close, which the merge fn would otherwise treat as an authoritative
        // close of this freshly-initialized state (VX-3589).
        closeSignal: false,
      }));

      // Capture initial state snapshot for change detection
      // Note: studyClassification and ontologyVersion will be populated later by the classifier,
      // so we capture them as empty here. The snapshot will be updated once classification is complete.
      setInitialFormSnapshot(initialFormState);

      // Capture initial step configurations (use the same object we set to stepConfigurations)
      setInitialStepSnapshot(initialSteps);

      // Sync the viewer layout to match the opening step's layout so the grid reflects the correct
      // dimensions when editing begins. The opening step, not step 0 -- see `initialStepIndex` above.
      if (initialSteps != null && initialSteps.length > 0) {
        applyViewerLayouts(stepLayoutsToLayouts(initialSteps[initialStepIndex].layouts));
      }
    };

    if (mode === SUBMIT_FORM_INTENT.SAVE) {
      // Create mode: start with a single default step, seeded with the current viewer layouts
      const defaultStep: StepConfiguration = {
        ...DEFAULT_CREATE_HP_STEP,
        layouts: layoutsToStepLayouts(layouts),
      };
      finishInit([defaultStep]);
    } else if (mode === SUBMIT_FORM_INTENT.UPDATE) {
      // Always fetch raw HP for complete, unfiltered viewport data.
      // liveStepConfigurations (from the matched query) has filtered viewport
      // entries — viewports whose criteria don't match the current case are
      // stripped. The raw fetch preserves all viewport configurations.
      //
      // After fetching, merge with liveStepConfigurations to pick up clinical
      // edits (adds, deletes, renames, reorders) that only exist in the live
      // Recoil atom and haven't been persisted to the backend yet.
      const fetchAndInit = async () => {
        let fetchedSteps: StepConfiguration[] | null = null;
        if (hangingProtocol?.smid) {
          try {
            const { data } = await fetchHangingProtocol({
              variables: { id: hangingProtocol.smid },
            });
            const rawSteps = (data?.hangingProtocol?.steps ?? []).map(stepFromGqlFormat);
            if (rawSteps.length > 0) fetchedSteps = rawSteps;
          } catch (err) {
            logger.warn('Failed to fetch raw HP for form init, using matched data', { err });
          }
        }
        // Fall back to the matched HP data if the direct fetch returned no steps
        if (fetchedSteps == null) {
          const existingSteps = (hangingProtocol?.steps ?? []).map(stepFromGqlFormat);
          fetchedSteps = existingSteps.length > 0 ? existingSteps : null;
        }

        // Merge: use raw-fetched viewport data for persisted steps, but
        // include clinical-only steps (adds) from liveStepConfigurations.
        let mergedSteps = fetchedSteps;
        if (
          liveStepConfigurations != null &&
          liveStepConfigurations.length > 0 &&
          fetchedSteps != null
        ) {
          // Build a map of fetched steps by smid for viewport data lookup
          const fetchedBySmid = new Map(
            fetchedSteps.filter((s) => s.smid != null).map((s) => [s.smid, s])
          );

          // For each live step: merge fetched viewport data (complete, unfiltered) with any
          // live viewport that isn't yet persisted (e.g. a clinical drag-and-drop edit) so
          // neither an unmatched criteria definition nor an unsaved edit is lost (VX-4614).
          // Preserve live step order, names, and count (reflects clinical edits).
          mergedSteps = liveStepConfigurations.map((liveStep) => {
            const fetched = liveStep.smid != null ? fetchedBySmid.get(liveStep.smid) : null;
            if (fetched != null) {
              return {
                ...liveStep,
                viewports: mergeStepViewportsForUpdate(liveStep.viewports, fetched.viewports),
                // Preserve live step's display_name (may have been renamed clinically)
              };
            }
            // Clinical-only step (not yet persisted) — use live data as-is
            return liveStep;
          });
        } else if (liveStepConfigurations != null && liveStepConfigurations.length > 0) {
          // No fetched data available — use live state
          mergedSteps = liveStepConfigurations;
        }

        // The form may have been closed, reopened, or switched to another HP while this fetch
        // was outstanding. A superseded pass must not write its steps into the shared atom.
        if (initGenerationRef.current !== initGeneration) return;

        finishInit(mergedSteps);
      };
      fetchAndInit();
    }
  }, [
    hangingProtocol,
    liveStepConfigurations,
    layouts,
    mode,
    primaryStudy?.description,
    setFormState,
    initializeSteps,
    setInitialFormSnapshot,
    setInitialStepSnapshot,
    applyViewerLayouts,
    fetchHangingProtocol,
  ]);

  // Recovery for an init pass that had nothing to seed from.
  //
  // `finishInit(null)` is a legitimate outcome on its own -- there was genuinely nothing to show --
  // but that pass has already claimed `lastInitializedFormKeyRef`, so the key guard turns away every
  // later pass for the same protocol and the Steps section stays empty for the rest of this open,
  // however much state arrives afterwards. The form then renders no chips while the protocol and the
  // clinical step bar both hold the steps.
  //
  // Reaching "nothing to seed from" needs both halves, which is why it surfaced intermittently
  // rather than always: `mergedSteps` defaults to the fetched steps and is only replaced when the
  // live atom is non-empty, so the pass has to fetch nothing *and* see an empty live atom. A raw
  // fetch that throws is caught and warned, leaving exactly that whenever the pass also runs before
  // the viewer's step atom is populated.
  //
  // Deliberately narrow. It fires only after the init pass has finished and still left the form with
  // no steps while the viewer has some, adopts exactly what the init merge would have chosen in that
  // situation, and issues no fetch -- so it cannot become a re-initialisation loop, and it cannot
  // overwrite in-progress edits, because a form with edits has steps.
  //
  // Waiting for the pass to *settle* is the whole point, and the key ref cannot report that: init
  // claims it synchronously and only then awaits the fetch, so a recovery gated on the key alone
  // fires about a millisecond into every open and seeds the form roughly two seconds before the
  // fetch it was supposed to be a fallback for. That is not just wasted work -- steps arriving that
  // much earlier move `StepsConfigSection`'s auto-expand earlier with them, which is enough to flip
  // the outcome of anything timed against the section opening. Compare the generations instead.
  useEffect(() => {
    if (mode !== SUBMIT_FORM_INTENT.UPDATE) return;
    // Init has not run, or the pass in flight has yet to finish; let it do the seeding.
    if (lastInitializedFormKeyRef.current == null) return;
    if (settledInitGenerationRef.current !== initGenerationRef.current) return;
    if (stepOps.steps != null && stepOps.steps.length > 0) return;
    if (liveStepConfigurations == null || liveStepConfigurations.length === 0) return;
    initializeSteps(liveStepConfigurations);
  }, [mode, stepOps.steps, liveStepConfigurations, initializeSteps]);

  return (
    <Stack vertical stretchY className={className} css={formCss}>
      <Stack vertical css={headerCss}>
        <Text variant="display2">Hanging Protocol</Text>
        <Text
          css={css`
            font-size: 13px;
            color: ${Colors.gray8};
            padding-top: 4px;
          `}
        >
          {POWERED_BY_SIRONA_CLASSIFICATIONS}
        </Text>
      </Stack>

      <SectionDivider />

      <form
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0; /* Important for flex child to be scrollable */
        `}
        onSubmit={handleSubmit}
        data-testid="save-hanging-protocol-form"
      >
        <FormBodyScroll
          orientation={ScrollOrientation.Vertical}
          data-testid="hanging-protocol-form-scroll"
        >
          <Stack
            vertical
            css={css`
              gap: 16px;
              ${bodyCss}
            `}
          >
            <Stack vertical>
              <Stack
                vertical
                css={css`
                  gap: 16px;
                `}
              >
                <Field
                  invalid={nameError != null}
                  css={nameError != null ? nameErrorCss : undefined}
                >
                  <Text
                    css={css`
                      font-size: 16px;
                      line-height: 24px;
                      color: ${Colors.gray10};
                    `}
                  >
                    Name this protocol
                  </Text>
                  <FieldControl
                    iconRight1={
                      hasNameBeenManuallyEdited ? (
                        <Tooltip content="Revert to auto-generated name" placement="top">
                          <Replay css={iconCss} />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          content="Auto-generated from study description. You can edit this name."
                          placement="top"
                        >
                          <Info css={iconCss} />
                        </Tooltip>
                      )
                    }
                    onClickIconRight1={
                      hasNameBeenManuallyEdited
                        ? () => {
                            setNameError(null);
                            setFormState((prev) => ({
                              ...prev,
                              hangingProtocolName: formState.studyCriteriaSourceText,
                            }));
                            setHasNameBeenManuallyEdited(false);
                          }
                        : undefined
                    }
                    aria-label-icon-right-1={
                      hasNameBeenManuallyEdited ? 'Revert to auto-generated name' : undefined
                    }
                  >
                    <input
                      name="name"
                      required
                      data-testid="protocol-field"
                      onChange={(e) => handleProtocolNameChange(e.target.value)}
                      value={formState.hangingProtocolName}
                      placeholder="Name your hanging protocol"
                    />
                  </FieldControl>
                  {nameError && (
                    <FieldError>
                      {/* Same Text treatment as the classifier's error below, rather than FieldError's own: its
                          Tailwind text-xs resolves against this app's 10px root and lands at 7.5px, half the size of
                          the error the reader sees two fields down. Borrowing the component keeps the two in step
                          instead of pinning a second set of numbers here. */}
                      <Text variant="caption" color="error">
                        {nameError}
                      </Text>
                    </FieldError>
                  )}
                </Field>
                <StudyClassifierInput
                  value={formState.studyCriteriaSourceText ?? ''}
                  onClassificationChange={handleStudyClassificationChange}
                  onDescriptionChange={handleStudyCriteriaSourceTextChange}
                />
              </Stack>
              <Stack
                vertical
                stretchY
                css={css`
                  padding-top: 16px;
                `}
              >
                <Stack
                  alignY="center"
                  css={css`
                    gap: 6px;
                    padding-bottom: 8px;
                  `}
                >
                  <AutoAwesome
                    css={css`
                      width: 16px;
                      height: 16px;
                      flex-shrink: 0;
                      path {
                        fill: ${Colors.gray8};
                      }
                    `}
                  />
                  <Text
                    css={css`
                      font-size: 14px;
                      color: ${Colors.light};
                    `}
                  >
                    Matching classification criteria
                  </Text>
                </Stack>
                <Stack vertical stretchY>
                  <MatchingCriteriaScroll
                    orientation={ScrollOrientation.Vertical}
                    data-testid="matching-criteria-scroll"
                  >
                    <StudyClassificationDisplay studyClassification={studyClassification} />
                  </MatchingCriteriaScroll>
                </Stack>
              </Stack>

              <Stack
                vertical
                stretchY
                css={css`
                  padding-top: 16px;
                `}
              >
                <Stack
                  alignY="center"
                  css={css`
                    gap: 6px;
                    padding-bottom: 8px;
                  `}
                >
                  <AutoAwesome
                    css={css`
                      width: 16px;
                      height: 16px;
                      flex-shrink: 0;
                      path {
                        fill: ${Colors.gray8};
                      }
                    `}
                  />
                  <Text
                    css={css`
                      font-size: 14px;
                      color: ${Colors.light};
                    `}
                  >
                    Matching study descriptions
                  </Text>
                </Stack>
                <Stack
                  vertical
                  stretchY
                  css={css`
                    padding-bottom: ${Spacings.small}rem;
                  `}
                >
                  {matchingDescriptionsLoading ? (
                    <Stack alignX="center" alignY="center" stretchY>
                      <CircularProgress size={24} />
                    </Stack>
                  ) : (
                    <MatchingDescriptionsScroll
                      orientation={ScrollOrientation.Vertical}
                      data-testid="matching-descriptions-scroll"
                    >
                      <MatchingDescriptionsTable descriptions={matchingDescriptions} />
                    </MatchingDescriptionsScroll>
                  )}
                </Stack>
              </Stack>

              {isHPStepsEnabled && (
                <StepsConfigSection
                  {...stepOps}
                  defaultExpanded={stepOps.steps != null && stepOps.steps.length > 1}
                  onStepClick={switchToStep}
                  isSubmitting={isSubmitting}
                  onDuplicateError={setHasDuplicateStepNameError}
                />
              )}
            </Stack>
          </Stack>
        </FormBodyScroll>
        <SectionDivider />
        <Stack
          alignY="end"
          alignX="end"
          css={css`
            padding: 0 ${Spacings.small}rem 16px;
            flex-shrink: 0;
          `}
        >
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancelClick}
            data-testid="cancel-hanging-protocol-form"
            css={`
              border: none;
              margin-right: ${Spacings.small}rem !important;
            `}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            data-testid="submit-hanging-protocol-form"
            disabled={
              isSubmitting || isClassifying || nameError != null || hasDuplicateStepNameError
            }
          >
            {isSubmitting ? (
              <CircularProgress
                size={16}
                sx={{ color: Colors.gray8 }}
                data-testid="submit-hanging-protocol-spinner"
                css={css`
                  margin-right: ${Spacings.xsmall}rem;
                `}
              />
            ) : null}
            {isSubmitting
              ? 'Submitting...'
              : mode === SUBMIT_FORM_INTENT.UPDATE
                ? 'Update'
                : 'Create'}
          </Button>
        </Stack>
      </form>
      {showDiscardModal && (
        <HPConfirmationModal
          {...DISCARD_CHANGES_MODAL}
          dontShowAgain={skipDiscardConfirmation}
          onDontShowAgainChange={setSkipDiscardConfirmation}
          onPrimaryAction={handleDiscardChanges}
          onSecondaryAction={handleKeepEditing}
          onClose={handleKeepEditing}
        />
      )}
    </Stack>
  );
};

HangingProtocolsForm.displayName = 'HangingProtocolsFormV4';
HangingProtocolsFormInner.displayName = 'HangingProtocolsFormV4Inner';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HangingProtocolsForm);
